import {
  Typography,
  Card,
  Box,
  Button,
  useMediaQuery,
  Theme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import InfoRow from "../../../components/Common/InfoRow";
import { formatDateTime } from "../../../utils/paymentFormat";

// A document type as read (identity → Identity).
const typeLabel = (type?: string) => {
  const text = String(type || "").replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const OpenButton = ({ url }: { url?: string }) => {
  const { t } = useTranslation();
  if (!url) return null;
  return (
    <Button
      variant="text"
      color="primary"
      size="small"
      startIcon={<OpenInNewRoundedIcon />}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t("Open document")}
    </Button>
  );
};

const MobileCard = ({ document }) => {
  const { t } = useTranslation();
  return (
    <Card sx={{ mb: 2 }}>
      <Box px={2} py={1}>
        <InfoRow label={t("Uploaded on")}>{formatDateTime(document.dateLastUpdated)}</InfoRow>
        <InfoRow label={t("Name")}>{document.name}</InfoRow>
        <InfoRow label={t("Type")}>{typeLabel(document.type)}</InfoRow>
        {document.url && (
          <Box py={1} display="flex" justifyContent="flex-end">
            <OpenButton url={document.url} />
          </Box>
        )}
      </Box>
    </Card>
  );
};

// The documents uploaded with the transaction (identity, proofs).
function InfoDocumentTab({ documents }: { documents: any[] }) {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));

  if (documents.length === 0) return null;

  if (isMobile) {
    return (
      <Box>
        {documents.map((document) => (
          <MobileCard key={document.id} document={document} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box pb={2}>
        <Typography variant="h6">{t("Documents")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Documents uploaded for this transaction.")}
        </Typography>
      </Box>
      <AnimatedBlock>
        <Card>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Date")}</TableCell>
                  <TableCell>{t("Name")}</TableCell>
                  <TableCell>{t("Type")}</TableCell>
                  <TableCell align="right">{t("Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(document.dateLastUpdated)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{document.name}</TableCell>
                    <TableCell>{typeLabel(document.type)}</TableCell>
                    <TableCell align="right">
                      <OpenButton url={document.url} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </AnimatedBlock>
    </Box>
  );
}

export default InfoDocumentTab;
